import api from '../../utils/helpers/api'
import type {
  ApiMetaWrapper,
  PaginatedData,
  LaporanKegiatan,
  LaporanKegiatanParams,
  LaporanKegiatanFormData,
  GroupedLaporan,
} from '../../types/pana'

type ResponType = {
  data: any
  meta: object
}

const laporanKegiatan = {
  /**
   * Get list laporan kegiatan with pagination
   */
  getList: async (params?: LaporanKegiatanParams): Promise<ResponType> => {
    return (await api.get('/pana-laporan-kegiatan', {params})) as Promise<ResponType>
  },

  /**
   * Get all laporan kegiatan without pagination
   */
  getAll: async (params?: Partial<LaporanKegiatanParams>): Promise<ResponType> => {
    return (await api.get('/pana-laporan-kegiatan', {params})) as Promise<ResponType>
  },

  /**
   * Get laporan kegiatan grouped by minggu (week)
   */
  getGroupedByMinggu: async (): Promise<ResponType> => {
    return (await api.get('/pana-laporan-kegiatan/my-laporan-grouped')) as Promise<ResponType>
  },

  /**
   * Get detail laporan kegiatan by ID
   */
  show: async (id: number): Promise<ResponType> => {
    return (await api.get(`/pana-laporan-kegiatan/${id}`)) as Promise<ResponType>
  },

  /**
   * Create new laporan kegiatan with files
   */
  create: async (data: LaporanKegiatanFormData): Promise<ResponType> => {
    const formData = new FormData()

    // Required fields
    formData.append('pendaftaran_pana_id', data.pendaftaran_pana_id.toString())
    formData.append('judul', data.judul)
    formData.append('deskripsi', data.deskripsi)
    formData.append('minggu_ke', data.minggu_ke.toString())

    // Optional fields
    if (data.link) {
      formData.append('link', data.link)
    }

    // Multiple files
    if (data.files && data.files.length > 0) {
      data.files.forEach((file) => {
        formData.append('files[]', file)
      })
    }

    return (await api.post('/pana-laporan-kegiatan', formData)) as Promise<ResponType>
  },

  /**
   * Update laporan kegiatan
   */
  update: async (id: number, data: Partial<LaporanKegiatanFormData>): Promise<ResponType> => {
    const formData = new FormData()

    // Optional fields for update
    if (data.judul) formData.append('judul', data.judul)
    if (data.deskripsi) formData.append('deskripsi', data.deskripsi)
    if (data.link) formData.append('link', data.link)
    if (data.minggu_ke) formData.append('minggu_ke', data.minggu_ke.toString())

    // Multiple files
    if (data.files && data.files.length > 0) {
      data.files.forEach((file) => {
        formData.append('files[]', file)
      })
    }

    // Use POST method directly (no method spoofing)
    return (await api.post(`/pana-laporan-kegiatan/${id}`, formData)) as Promise<ResponType>
  },

  /**
   * Delete laporan kegiatan
   */
  delete: async (id: number): Promise<ResponType> => {
    return (await api.delete(`/pana-laporan-kegiatan/${id}`)) as Promise<ResponType>
  },

  /**
   * Add files to existing laporan kegiatan
   */
  addFiles: async (id: number, files: File[]): Promise<ResponType> => {
    const formData = new FormData()

    files.forEach((file) => {
      formData.append('files[]', file)
    })

    return (await api.post(`/pana-laporan-kegiatan/${id}/add-files`, formData)) as Promise<
      ResponType
    >
  },

  /**
   * Delete file from laporan kegiatan
   */
  deleteFile: async (laporanId: number, fileId: number): Promise<ResponType> => {
    return (await api.delete(
      `/pana-laporan-kegiatan/${laporanId}/files/${fileId}`
    )) as Promise<ResponType>
  },
}

export default laporanKegiatan
